import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type QueryClient } from "@tanstack/react-query";
import { keyWithPrincipal } from "@/queries/principal-key";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    ListPrebuildsRequest,
    ListPrebuildsRequest_Filter,
    type ListPrebuildsResponse,
    type Prebuild,
    CreatePrebuildRequest,
    PrebuildSpec,
    CancelPrebuildRequest,
    DeletePrebuildRequest,
} from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";

export const prebuildQueryKeys = {
    listForProject: (projectId?: string) => keyWithPrincipal(["prebuilds", "list", { projectId }]),
    listForProjects: (projectIds?: string[]) => keyWithPrincipal(["prebuilds", "list", { projectIds }]),
    get: (prebuildId?: string) => keyWithPrincipal(["prebuilds", { prebuildId }]),
    logsToken: (prebuildId?: string, phase?: number) =>
        keyWithPrincipal(["prebuilds", "logsToken", { prebuildId, phase }]),
};

export type PlainPrebuild = PlainMessage<Prebuild>;

export const useProjectPrebuilds = (projectId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: prebuildQueryKeys.listForProject(projectId),
        queryFn: async () => {
            if (!projectId) {
                return { prebuilds: [], pagination: undefined };
            }

            const prebuilds = await autoPaginate<ListPrebuildsResponse, ListPrebuildsRequest, Prebuild>(
                (params) => api.prebuildService.listPrebuilds(params),
                new ListPrebuildsRequest({
                    filter: new ListPrebuildsRequest_Filter({
                        projectIds: [projectId],
                    }),
                }),
                (response) => response.prebuilds,
            );

            return {
                prebuilds: prebuilds.map(toPlainPrebuild),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!projectId,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60, // 1 hour

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const toPlainPrebuild = (prebuild: Prebuild): PlainPrebuild => {
    return toPlainMessage(prebuild);
};

/**
 * Represents the latest prebuilds for a project, grouped by environment class.
 */
export type ProjectPrebuildsStatus = {
    /** Latest prebuild per environment class ID */
    byEnvironmentClass: Map<string, PlainPrebuild>;
    /** All latest prebuilds for this project (one per environment class) */
    all: PlainPrebuild[];
};

/**
 * Fetches the latest prebuild for each environment class for each project.
 * Returns a map of projectId -> ProjectPrebuildsStatus.
 * Auto-paginates to ensure we get all prebuilds across all projects.
 *
 * The ListPrebuilds API limits project_ids to 25 per request, so we batch
 * into chunks and merge results.
 */
export const useLatestPrebuildsByProjects = (projectIds?: string[]) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: prebuildQueryKeys.listForProjects(projectIds),
        queryFn: async () => {
            if (!projectIds || projectIds.length === 0) {
                return new Map<string, ProjectPrebuildsStatus>();
            }

            // The API validates max 25 project_ids per request, so batch into chunks
            const PROJECT_IDS_BATCH_SIZE = 25;
            const chunks: string[][] = [];
            for (let i = 0; i < projectIds.length; i += PROJECT_IDS_BATCH_SIZE) {
                chunks.push(projectIds.slice(i, i + PROJECT_IDS_BATCH_SIZE));
            }

            // Fetch all chunks in parallel, each auto-paginated
            const chunkResults = await Promise.all(
                chunks.map((chunk) =>
                    autoPaginate<ListPrebuildsResponse, ListPrebuildsRequest, Prebuild>(
                        (params) => api.prebuildService.listPrebuilds(params),
                        new ListPrebuildsRequest({
                            filter: new ListPrebuildsRequest_Filter({
                                projectIds: chunk,
                            }),
                        }),
                        (response) => response.prebuilds,
                    ),
                ),
            );
            const prebuilds = chunkResults.flat();

            // Group prebuilds by project, then by environment class
            // Keep only the latest per environment class (first one, since API returns newest first)
            const result = new Map<string, ProjectPrebuildsStatus>();

            for (const prebuild of prebuilds) {
                const projectId = prebuild.metadata?.projectId;
                const envClassId = prebuild.metadata?.environmentClassId;
                if (!projectId || !envClassId) continue;

                let projectStatus = result.get(projectId);
                if (!projectStatus) {
                    projectStatus = { byEnvironmentClass: new Map(), all: [] };
                    result.set(projectId, projectStatus);
                }

                // Only keep the latest prebuild per environment class
                if (!projectStatus.byEnvironmentClass.has(envClassId)) {
                    const plainPrebuild = toPlainPrebuild(prebuild);
                    projectStatus.byEnvironmentClass.set(envClassId, plainPrebuild);
                    projectStatus.all.push(plainPrebuild);
                }
            }

            return result;
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!projectIds && projectIds.length > 0,
        staleTime: 1_000 * 60 * 2, // 2 minutes
        gcTime: 1_000 * 60 * 30, // 30 minutes

        // refetch on reconnect, window focus, and every 2 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: 1_000 * 60 * 2, // 2 minutes
    });
};

export const usePrebuild = (prebuildId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainPrebuild, Error>({
        queryKey: prebuildQueryKeys.get(prebuildId),
        queryFn: async (): Promise<PlainPrebuild> => {
            if (!prebuildId) {
                throw new Error("No prebuild ID provided");
            }

            const { prebuild } = await api.prebuildService.getPrebuild({ prebuildId });
            if (!prebuild) {
                throw new Error("Prebuild not found");
            }

            return toPlainPrebuild(prebuild);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!prebuildId,
        staleTime: 5_000,
        gcTime: 1_000 * 60 * 5, // 5 minutes
    });
};

export type CreatePrebuildParams = {
    projectId: string;
    environmentClassId?: string;
};

export const useCreatePrebuild = (): UseMutationResult<PlainPrebuild, Error, CreatePrebuildParams, unknown> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation<PlainPrebuild, Error, CreatePrebuildParams>({
        mutationFn: async (params: CreatePrebuildParams) => {
            const request = new CreatePrebuildRequest({
                projectId: params.projectId,
                environmentClassId: params.environmentClassId,
                spec: new PrebuildSpec({}),
            });
            const { prebuild } = await api.prebuildService.createPrebuild(request);
            if (!prebuild) {
                throw new Error("Failed to create prebuild");
            }
            return toPlainPrebuild(prebuild);
        },
        onSuccess: (_created, variables) => {
            // Refresh the list of prebuilds for this project
            queryClient
                .invalidateQueries({
                    queryKey: prebuildQueryKeys.listForProject(variables.projectId),
                })
                .catch(() => void 0);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type DeletePrebuildParams = {
    prebuildId: string;
    projectId: string;
};

export const useDeletePrebuild = (): UseMutationResult<void, Error, DeletePrebuildParams, unknown> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation<void, Error, DeletePrebuildParams>({
        mutationFn: async (params: DeletePrebuildParams) => {
            const request = new DeletePrebuildRequest({
                prebuildId: params.prebuildId,
            });
            await api.prebuildService.deletePrebuild(request);
        },
        onSuccess: (_result, variables) => {
            // Refresh the list of prebuilds for this project
            queryClient
                .invalidateQueries({
                    queryKey: prebuildQueryKeys.listForProject(variables.projectId),
                })
                .catch(() => void 0);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCreatePrebuildLogsToken = (prebuildId?: string, phase?: number) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes

    return useQuery<string, Error>({
        queryKey: prebuildQueryKeys.logsToken(prebuildId, phase),
        queryFn: async (): Promise<string> => {
            if (!prebuildId) {
                throw new Error("No prebuild ID provided");
            }

            const { accessToken } = await api.prebuildService.createPrebuildLogsToken({ prebuildId });
            if (!accessToken) {
                throw new Error("No token provided");
            }

            return accessToken;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!prebuildId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelPrebuild = (): UseMutationResult<PlainPrebuild, Error, string, unknown> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation<PlainPrebuild, Error, string>({
        mutationFn: async (prebuildId: string) => {
            const request = new CancelPrebuildRequest({
                prebuildId,
            });
            const { prebuild } = await api.prebuildService.cancelPrebuild(request);
            if (!prebuild) {
                throw new Error("Failed to cancel prebuild");
            }
            return toPlainPrebuild(prebuild);
        },
        onSuccess: (prebuild) => {
            // Refresh the list of prebuilds for this project
            if (prebuild.metadata?.projectId) {
                queryClient
                    .invalidateQueries({
                        queryKey: prebuildQueryKeys.listForProject(prebuild.metadata.projectId),
                    })
                    .catch(() => void 0);
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const handlePrebuildEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    console.debug(`[EventHandler] Prebuild event:`, {
        operation: evt.operation,
        resourceId: evt.resourceId,
    });

    if (evt.operation === ResourceOperation.UPDATE) {
        const prebuild = await refetchPrebuild(api, evt.resourceId);
        if (prebuild) {
            const plainPrebuild = toPlainPrebuild(prebuild);
            // Update the individual prebuild cache
            client.setQueryData(prebuildQueryKeys.get(evt.resourceId), plainPrebuild);
            // Update the prebuild in the list cache for its project
            if (plainPrebuild.metadata?.projectId) {
                await client.invalidateQueries({
                    queryKey: prebuildQueryKeys.listForProject(plainPrebuild.metadata.projectId),
                });
            }
        }
        console.debug(`[EventHandler] Prebuild ${evt.resourceId} updated in cache`);
    }
    if (evt.operation === ResourceOperation.CREATE) {
        // Invalidate all prebuild list queries
        await client.invalidateQueries({ queryKey: keyWithPrincipal(["prebuilds", "list"]) });
        console.debug(`[EventHandler] Prebuild ${evt.resourceId} created, lists invalidated`);
    }
    if (evt.operation === ResourceOperation.DELETE) {
        // Remove from individual cache
        client.setQueryData(prebuildQueryKeys.get(evt.resourceId), undefined);
        // Invalidate all prebuild list queries
        await client.invalidateQueries({ queryKey: keyWithPrincipal(["prebuilds", "list"]) });
        console.debug(`[EventHandler] Prebuild ${evt.resourceId} deleted, lists invalidated`);
    }
};

const refetchPrebuild = async (api: GitpodAPI, prebuildId: string) => {
    try {
        const response = await api.prebuildService.getPrebuild({ prebuildId });
        return response.prebuild;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        // this might happen if the prebuild was deleted in the meantime
        if (!isNotFound) {
            console.error("Failed to refetch prebuild", prebuildId, error);
        }
    }
};
